/* Xiaomi 2026 · Benefit board (Ayush's stage-column pattern)
 *
 * Renders 4-5 visible columns sourced from xiaomi_lifecycle_stage_maps.
 * Each column shows partner cards · click a card to open detail/audit
 * actions (retry · cancel · CSV export at board level).
 *
 * Column-overflow safety: shows first 8 cards · "+ N more" chip
 * if a stage has > 8 instances. Spec §7.1 in integration memo.
 *
 * Day 3 (Sprint 2A): retry + cancel + CSV export wired through.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const LIFECYCLE_TITLES = {
  gift_15_step: 'Gift journeys',
  service_redemption_15_step: 'Service journeys',
  event_invite_15_step: 'Event journeys',
  staycation_15_step: 'Staycation journeys',
  zsm_gift_15_step: 'ZSM gift',
};


export default function XiaomiBenefitBoard() {
  const { lifecycleKey } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeInstance, setActiveInstance] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/xiaomi/workflow/board/${lifecycleKey}`, auth(),
      );
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Board unavailable');
    } finally {
      setLoading(false);
    }
  }, [lifecycleKey]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/xiaomi/workflow/instances/export.csv?lifecycle_key=${lifecycleKey}&state=open`,
        { ...auth(), responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xiaomi_${lifecycleKey}_open_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('CSV export failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-soft text-sm">Loading board…</div>;
  if (err) return <div className="x-card-soft px-4 py-3 text-sm">{err}</div>;

  const stages = data?.stages || [];
  const title = LIFECYCLE_TITLES[lifecycleKey] || lifecycleKey;

  return (
    <div data-testid="xiaomi-benefit-board">
      <div className="flex items-end justify-between mb-2">
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif' }}>{title}</h1>
          <p className="text-soft text-sm">
            {data?.total_open ?? 0} open instances across {stages.length} stages
          </p>
        </div>
        <button
          className="btn-ghost"
          onClick={exportCsv}
          disabled={busy}
          data-testid="xiaomi-board-export-csv"
        >
          ⇩ Export CSV
        </button>
      </div>

      <div
        className="grid gap-3 pipe-horizontal"
        style={{ gridTemplateColumns: `repeat(${stages.length || 1}, minmax(0, 1fr))` }}
      >
        {stages.map((sec) => (
          <StageColumn key={sec.stage.key} section={sec} onCardClick={setActiveInstance} />
        ))}
      </div>

      {activeInstance && (
        <InstanceActionsDrawer
          instance={activeInstance}
          onClose={() => setActiveInstance(null)}
          onActed={() => { setActiveInstance(null); load(); }}
        />
      )}
    </div>
  );
}


function StageColumn({ section, onCardClick }) {
  const { stage, count, instances } = section;
  const shown = instances.slice(0, 8);
  const more = Math.max(0, count - shown.length);

  return (
    <div
      className="x-card-soft p-3"
      data-testid={`xiaomi-stage-col-${stage.key}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`stage stage-${stage.status_class}`}>{stage.label}</span>
        <span className="text-soft text-xs font-semibold">{count}</span>
      </div>
      <div className="space-y-2">
        {shown.map((inst) => (
          <PartnerCard
            key={inst._id}
            instance={inst}
            onClick={() => onCardClick(inst)}
          />
        ))}
        {more > 0 && (
          <div
            className="text-xs text-soft text-center py-2 border border-dashed border-line rounded"
            data-testid={`xiaomi-stage-overflow-${stage.key}`}
          >
            + {more} more
          </div>
        )}
        {count === 0 && (
          <div className="text-xs text-soft text-center py-6">No partners in this stage</div>
        )}
      </div>
    </div>
  );
}


function PartnerCard({ instance, onClick }) {
  return (
    <div
      className="bg-panel border border-line rounded-lg p-2.5 cursor-pointer hover:shadow transition"
      onClick={onClick}
      data-testid={`xiaomi-partner-card-${instance._id}`}
    >
      <div className="text-sm font-semibold truncate">{instance.dealer_id}</div>
      <div className="text-[10px] text-soft mt-0.5">
        {instance.tier_at_creation || '—'} · {instance.language_used || 'en'}
        {instance.retry_count > 0 && (
          <span className="ml-1" style={{ color: '#d98a1f' }}>
            · ↻ {instance.retry_count}
          </span>
        )}
      </div>
    </div>
  );
}


function InstanceActionsDrawer({ instance, onClose, onActed }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null);   // 'retry' | 'cancel'

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Reason required for audit log');
      return;
    }
    setBusy(true);
    try {
      const endpoint = action === 'retry'
        ? `${API}/xiaomi/workflow/instance/${instance._id}/retry`
        : `${API}/xiaomi/workflow/instance/${instance._id}/cancel`;
      const r = await axios.post(endpoint, { reason }, auth());
      if (action === 'retry') {
        const ok = r.data.action_result?.ok;
        if (ok) toast.success(`Retry fired · action ${r.data.retried_action}`);
        else toast.error(`Retry attempted · action returned: ${r.data.action_result?.error || 'not-ok'}`);
      } else {
        toast.success('Instance cancelled · audit logged');
      }
      onActed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      style={{ background: 'rgba(20,15,8,0.45)' }}
      onClick={onClose}
    >
      <div
        className="x-modal flex flex-col w-full h-full"
        style={{ maxWidth: 480, borderRadius: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-line">
          <div className="text-[11px] uppercase tracking-wider text-soft">Instance actions</div>
          <h2 style={{ fontFamily: 'Fraunces, serif' }} className="mt-1">
            {instance.dealer_id}
          </h2>
          <div className="text-[12px] text-soft mt-1 font-mono">{instance._id}</div>
          <div className="text-[11px] text-soft mt-1">
            Step {instance.current_step} · {instance.state}
            {instance.retry_count > 0 && ` · retried ${instance.retry_count}x`}
          </div>
        </div>

        <div className="p-6 flex-1 overflow-auto space-y-4">
          {!action && (
            <>
              <p className="text-sm text-ink">What do you want to do?</p>
              <button
                type="button"
                className="x-card-soft p-4 w-full text-left hover:bg-[#fff3e0] transition"
                onClick={() => setAction('retry')}
                disabled={instance.state !== 'open'}
                data-testid="xiaomi-instance-action-retry"
              >
                <div className="font-semibold text-ink">↻ Retry current step</div>
                <div className="text-[12px] text-soft mt-1">
                  Re-fires the WhatsApp send / courier dispatch / email for this step
                  without rewinding the state machine. Use when delivery failed transiently.
                </div>
              </button>
              <button
                type="button"
                className="x-card-soft p-4 w-full text-left hover:bg-[#fbe9e3] transition"
                onClick={() => setAction('cancel')}
                disabled={instance.state === 'closed'}
                data-testid="xiaomi-instance-action-cancel"
                style={{ borderColor: '#f0c4b8' }}
              >
                <div className="font-semibold" style={{ color: '#c0492f' }}>✕ Cancel instance</div>
                <div className="text-[12px] text-soft mt-1">
                  Force-close this instance · jumps to step 15 with outcome
                  <code className="ml-1">force_closed</code>. CEO / xiaomi_admin only.
                  Cannot be undone, but is fully audit-logged.
                </div>
              </button>
            </>
          )}

          {action && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-soft">
                {action === 'retry' ? 'Retry reason' : 'Cancel reason'}
              </div>
              <textarea
                autoFocus
                className="x-input w-full"
                style={{ minHeight: 120, fontSize: 13.5, fontFamily: 'var(--fnt-body)' }}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={action === 'retry'
                  ? 'e.g. "WhatsApp returned NetworkError last attempt"'
                  : 'e.g. "Partner left the programme · per Xiaomi RSM email 28-May"'}
                data-testid={`xiaomi-instance-${action}-reason`}
              />
              <p className="text-[11px] text-soft">
                Reason is captured in the audit log alongside your email + the
                current step. Required for accountability.
              </p>
            </>
          )}
        </div>

        <div className="p-4 border-t border-line flex justify-end gap-2 bg-panel2">
          {action && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => { setAction(null); setReason(''); }}
              disabled={busy}
            >
              ← Back
            </button>
          )}
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
          {action && (
            <button
              type="button"
              className={action === 'cancel' ? 'btn-dark' : 'btn-mi'}
              onClick={submit}
              disabled={busy || !reason.trim()}
              data-testid={`xiaomi-instance-${action}-submit`}
              style={action === 'cancel' ? { background: '#c0492f' } : undefined}
            >
              {busy ? 'Working…' : (action === 'retry' ? '↻ Retry now' : '✕ Cancel instance')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
